import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb, Col, Row, Select, Space, Tabs, Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { FaFileInvoice, FaMoneyBillWave, FaReceipt, FaTicketAlt } from 'react-icons/fa';

import AppUserForm from '../components/AppUserForm';
import Loading from '../../../components/Loading';
import PageHeader from '../../../components/PageHeader';
import useViewAppUser from './useViewAppUser';
import { poeValorComPontosEmMil, convertMoedaBRDoJSNovo, formatDateStringToDateAndTime } from '../../../utils/Conversores';
import { formatCpf, formatPhone } from '../../../utils/Masks';
import { ThemeContext } from '../../../contexts/ThemeContext';

import ModalGiveawayTickets from '../components/ModalGiveawayTickets';
import ModalInvoicesInProcessing from '../components/ModalInvoicesInProcessing';
import ModalInvoiceDetails from '../components/ModalInvoiceDetails';
import TabInvoices from './components/Tabs/TabInvoices';
import TabGiveaways from './components/Tabs/TabGiveaways';
import TabVouchers from './components/Tabs/TabVouchers';
import TabTaxes from './components/Tabs/TabTaxes';
import CardStats from './components/CardStats';

const { Option } = Select;

export default function ViewAppUser() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        isLoading,
        user,
        handleSubmit,
        totalProcessingInvoices,
        invoices,
        showModalInvoice,
        handleShowModalInvoice,
        invoicesByMonth,
        loadInvoicesByPeriod,
        loadingInvoices,
        showModalTickets,
        handleShowModalTickets,
        selectedGiveaway,
        filteredGiveaways,
        handleSetSelectedGiveaway,
        filterGiveaways,
        handleSetFilterGiveaway,
        statsUser,
        vouchers,
        invoicesInProcessing,
        loadingInvoicesInProcessing,
        reprocessInvoice,
        loadingInvoicesReprocess,
        handleClickViewInvoicesInProcessing,
        showModalInvoiceInProcessing,
        handleShowModalInvoiceInProcessing,
        taxes,
        selectedYear,
        availableYears,
        setSelectedYear,
    } = useViewAppUser();

    const tabs = [
        {
            key: '1',
            label: 'Dados do Usuário',
            children: (
                <div style={{ paddingTop: 8 }}>
                    <AppUserForm action='Visualizar' user={user} key={user?.id} onSubmit={handleSubmit} />
                </div>
            ),
        },
    ];

    tabs.push({
        key: '2',
        label: 'Notas Fiscais',
        children: (
            <TabInvoices
                totalProcessingInvoices={totalProcessingInvoices}
                invoices={invoices}
                loadInvoicesByPeriod={loadInvoicesByPeriod}
                handleShowModalInvoice={handleShowModalInvoice}
                handleClickViewInvoicesInProcessing={handleClickViewInvoicesInProcessing}
            />
        ),
    });

    if (filteredGiveaways.length > 0) {
        tabs.push({
            key: '3',
            label: 'Sorteios',
            children: (
                <TabGiveaways
                    filteredGiveaways={filteredGiveaways}
                    filterGiveaways={filterGiveaways}
                    handleSetSelectedGiveaway={handleSetSelectedGiveaway}
                    handleShowModalTickets={handleShowModalTickets}
                    handleSetFilterGiveaway={handleSetFilterGiveaway}
                />
            ),
        });
    }

    if (taxes && taxes.length > 0) {
        tabs.push({
            key: '4',
            label: 'Impostos',
            children: <TabTaxes taxes={taxes} />,
        });
    }

    if (vouchers && vouchers.length > 0) {
        tabs.push({
            key: '5',
            label: 'Vouchers',
            children: <TabVouchers vouchers={vouchers} />,
        });
    }

    return (
        <Loading loading={isLoading}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* External Breadcrumb Card */}
                <div
                    style={{
                        background: isDark ? '#141414' : '#ffffff',
                        borderRadius: 10,
                        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                        boxShadow: isDark
                            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                            : '0 2px 10px rgba(0, 0, 0, 0.03)',
                        padding: '10px 16px',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Breadcrumb
                        items={[
                            {
                                title: <Link to='/usuarios/aplicativo'>Usuários do Aplicativo</Link>,
                            },
                            {
                                title: user?.name || `Visualizar Usuário`,
                            }
                        ]}
                    />
                </div>

                <PageHeader
                    icon={<UserOutlined />}
                    title={user?.name || "Visualizar Usuário do Aplicativo"}
                    subtitle={
                        user
                            ? `CPF: ${formatCpf(user.cpf)} • Telefone: ${formatPhone(user.phone)}${user.email ? ` • Email: ${user.email}` : ''}`
                            : "Detalhes cadastrais, notas fiscais, sorteios e estatísticas do cidadão"
                    }
                    tag={
                        user?.last_access ? (
                            <Tag color="blue" style={{ borderRadius: 6 }}>
                                Último acesso: {formatDateStringToDateAndTime(user.last_access, true)}
                            </Tag>
                        ) : undefined
                    }
                    actions={
                        <Space align="center" wrap>
                            {availableYears.length > 1 && (
                                <Space align="center">
                                    <span style={{ fontSize: 13, color: isDark ? '#8c8c8c' : '#64748b', fontWeight: 500 }}>
                                        Ano sorteio:
                                    </span>
                                    <Select
                                        value={selectedYear}
                                        onChange={setSelectedYear}
                                        style={{ minWidth: 100 }}
                                        size="middle"
                                    >
                                        <Option value="todos">Todos</Option>
                                        {availableYears
                                            .filter(year => year !== 'todos')
                                            .map(year => (
                                                <Option key={year} value={year}>
                                                    {year}
                                                </Option>
                                            ))}
                                    </Select>
                                </Space>
                            )}
                        </Space>
                    }
                />

                <Row gutter={[12, 12]}>
                    <Col xs={24} sm={12} lg={12} xl={6}>
                        <CardStats
                            title='Sorteios Participados'
                            value={statsUser?.total_raffle_with_tickets ?? 0}
                            icon={<FaTicketAlt />}
                            accentColor="#1677ff"
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={12} xl={6}>
                        <CardStats
                            title='Total de Bilhetes'
                            value={poeValorComPontosEmMil(statsUser?.total_tickets)}
                            icon={<FaReceipt />}
                            accentColor="#722ed1"
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={12} xl={6}>
                        <CardStats
                            title='Valor Total das Notas'
                            prefix="R$"
                            value={convertMoedaBRDoJSNovo(statsUser?.value_total_invoices)}
                            icon={<FaMoneyBillWave />}
                            accentColor="#10b981"
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={12} xl={6}>
                        <CardStats
                            title='Notas Importadas'
                            value={statsUser?.total_invoices ?? 0}
                            icon={<FaFileInvoice />}
                            accentColor="#f59e0b"
                        />
                    </Col>
                </Row>

                <div
                    style={{
                        background: isDark ? '#141414' : '#ffffff',
                        borderRadius: 14,
                        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                        boxShadow: isDark
                            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                            : '0 2px 10px rgba(0, 0, 0, 0.03)',
                        padding: '18px 20px',
                    }}
                >
                    <Tabs
                        defaultActiveKey='1'
                        items={tabs}
                    />
                </div>

                <ModalInvoiceDetails
                    open={showModalInvoice}
                    isLoading={loadingInvoices}
                    invoices={invoicesByMonth}
                    onClose={handleShowModalInvoice}
                />

                {selectedGiveaway && (
                    <ModalGiveawayTickets
                        open={showModalTickets}
                        appUser={user!}
                        giveawayId={selectedGiveaway.id}
                        onClose={handleShowModalTickets}
                        key={'ModalTickets'}
                    />
                )}

                <ModalInvoicesInProcessing
                    onReprocessInvoice={reprocessInvoice}
                    loadingReprocess={loadingInvoicesReprocess}
                    invoices={invoicesInProcessing}
                    onClose={handleShowModalInvoiceInProcessing}
                    open={showModalInvoiceInProcessing}
                    isLoading={loadingInvoicesInProcessing}
                />
            </div>
        </Loading>
    );
}
